/**
 * Map - Enhanced map component using native Google Maps API listeners
 * Provides viewport tracking and programmatic control via MapController
 */

import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  APIProvider,
  Map as GoogleMap,
  useMap,
} from "@vis.gl/react-google-maps";
import {
  MapProvider,
  type MapController,
  type MapViewport,
  type MapViewportUpdate,
  type MapError,
  type LatLng
} from "./MapContext";

// Enhanced map options interface
export interface MapOptions extends Omit<google.maps.MapOptions, 'center' | 'zoom'> {
  // Override with our enhanced types
  center?: LatLng
  zoom?: number
}

// Comprehensive map props interface
export interface MapProps {
  // Required props
  apiKey: string
  defaultCenter: LatLng
  defaultZoom: number
  
  // Optional styling
  className?: string
  style?: React.CSSProperties
  
  // Event handlers
  onViewportChange?: (viewport: MapViewport) => void
  onViewportSettle?: (viewport: MapViewport) => void
  onError?: (error: MapError) => void
  onLoad?: (map: google.maps.Map) => void
  
  // Advanced configuration
  options?: MapOptions
  children?: React.ReactNode
  
  // Accessibility
  ariaLabel?: string
  ariaDescription?: string
}

/**
 * Internal component that uses native Google Maps listeners
 */
// Internal props interface for MapWithListeners
interface MapWithListenersProps {
  onViewportChange?: (viewport: MapViewport) => void
  onViewportSettle?: (viewport: MapViewport) => void
  onControllerReady?: (controller: MapController) => void
  onError?: (error: MapError) => void
  onLoad?: (map: google.maps.Map) => void
}

function MapWithListeners({
  onViewportChange,
  onViewportSettle,
  onControllerReady,
  onError,
  onLoad
}: MapWithListenersProps) {
  const map = useMap()
  const lastViewportRef = useRef<MapViewport>()
  const mapWeakRef = useRef<WeakRef<google.maps.Map>>()

  // Validate coordinates
  const validateCoordinates = useCallback((lat: number, lng: number): boolean => {
    if (typeof lat !== 'number' || typeof lng !== 'number') {
      onError?.({
        type: 'INVALID_COORDINATES',
        message: 'Coordinates must be numbers',
        coordinates: { lat, lng }
      })
      return false
    }

    // Check for common invalid values first
    if (isNaN(lat) || isNaN(lng) || !isFinite(lat) || !isFinite(lng)) {
      onError?.({
        type: 'INVALID_COORDINATES',
        message: 'Coordinates cannot be NaN or infinite',
        coordinates: { lat, lng }
      })
      return false
    }

    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      onError?.({
        type: 'INVALID_COORDINATES',
        message: `Invalid coordinates: lat must be [-90, 90], lng must be [-180, 180]`,
        coordinates: { lat, lng }
      })
      return false
    }

    return true
  }, [onError])

  // Helper to get current viewport with error handling
  const getCurrentViewport = useCallback((): MapViewport | null => {
    if (!map) return null

    try {
      const center = map.getCenter()
      const zoom = map.getZoom()
      const bounds = map.getBounds()

      if (!center || zoom === undefined) {
        onError?.({
          type: 'VIEWPORT_ERROR',
          message: 'Unable to get map center or zoom level'
        })
        return null
      }

      const lat = center.lat()
      const lng = center.lng()

      // Validate the coordinates we got from the map
      if (!validateCoordinates(lat, lng)) {
        return null
      }

      return {
        center: { lat, lng },
        zoom,
        bounds: bounds || undefined
      }
    } catch (error) {
      onError?.({
        type: 'VIEWPORT_ERROR',
        message: 'Failed to get current viewport',
        originalError: error instanceof Error ? error : undefined
      })
      return null
    }
  }, [map, onError, validateCoordinates])

  // Helper to update map viewport programmatically with error handling
  const updateMapViewport = useCallback((viewport: MapViewportUpdate, animate = true) => {
    if (!map) {
      onError?.({
        type: 'VIEWPORT_ERROR',
        message: 'Cannot update viewport: map not available'
      })
      return
    }

    try {
      // Validate coordinates if provided
      if (viewport.center) {
        const lat = viewport.center.lat ?? map.getCenter()?.lat()
        const lng = viewport.center.lng ?? map.getCenter()?.lng()
        
        if (lat === undefined || lng === undefined) {
          onError?.({
            type: 'VIEWPORT_ERROR',
            message: 'Invalid center coordinates provided'
          })
          return
        }
        
        if (!validateCoordinates(lat, lng)) {
          return // Error already reported by validateCoordinates
        }
      }

      // Validate zoom level
      if (viewport.zoom !== undefined) {
        if (typeof viewport.zoom !== 'number' || viewport.zoom < 0 || viewport.zoom > 25 || !isFinite(viewport.zoom)) {
          onError?.({
            type: 'VIEWPORT_ERROR',
            message: `Invalid zoom level: ${viewport.zoom}. Must be between 0 and 25`
          })
          return
        }
      }

      // Apply viewport changes
      if (viewport.center && viewport.zoom !== undefined) {
        if (animate) {
          map.panTo(viewport.center)
          map.setZoom(viewport.zoom)
        } else {
          map.setCenter(viewport.center)
          map.setZoom(viewport.zoom)
        }
      } else if (viewport.center) {
        if (animate) {
          map.panTo(viewport.center)
        } else {
          map.setCenter(viewport.center)
        }
      } else if (viewport.zoom !== undefined) {
        map.setZoom(viewport.zoom)
      }

      if (viewport.bounds) {
        map.fitBounds(viewport.bounds, { animate })
      }
    } catch (error) {
      onError?.({
        type: 'VIEWPORT_ERROR',
        message: 'Failed to update map viewport',
        originalError: error instanceof Error ? error : undefined
      })
    }
  }, [map, onError, validateCoordinates])

  // Create controller and notify parent when ready, using WeakRef for map instance
  useEffect(() => {
    if (!map) return

    // Store map in WeakRef to prevent memory leaks
    mapWeakRef.current = new WeakRef(map)

    const controller: MapController = {
      // Viewport operations
      getCurrentViewport,
      updateMapViewport,
      
      // Navigation methods
      panTo: (center: LatLng, animate = true) => {
        if (!validateCoordinates(center.lat, center.lng)) return
        
        try {
          if (animate) {
            map.panTo(center)
          } else {
            map.setCenter(center)
          }
        } catch (error) {
          onError?.({
            type: 'PAN_ERROR',
            message: 'Failed to pan to coordinates',
            originalError: error instanceof Error ? error : undefined
          })
        }
      },
      
      panBy: (deltaX: number, deltaY: number) => {
        try {
          map.panBy(deltaX, deltaY)
        } catch (error) {
          onError?.({
            type: 'PAN_ERROR',
            message: `Failed to pan by ${deltaX}, ${deltaY}`,
            originalError: error instanceof Error ? error : undefined
          })
        }
      },
      
      setZoom: (zoom: number, animate = true) => {
        if (typeof zoom !== 'number' || zoom < 0 || zoom > 25 || !isFinite(zoom)) {
          onError?.({
            type: 'VIEWPORT_ERROR',
            message: `Invalid zoom level: ${zoom}`
          })
          return
        }
        
        try {
          map.setZoom(zoom)
        } catch (error) {
          onError?.({
            type: 'VIEWPORT_ERROR',
            message: 'Failed to set zoom level',
            originalError: error instanceof Error ? error : undefined
          })
        }
      },
      
      fitBounds: (bounds: google.maps.LatLngBounds, animate = true) => {
        try {
          map.fitBounds(bounds, { animate })
        } catch (error) {
          onError?.({
            type: 'VIEWPORT_ERROR',
            message: 'Failed to fit bounds',
            originalError: error instanceof Error ? error : undefined
          })
        }
      },
      
      // Map instance access
      getMapInstance: () => {
        const mapInstance = mapWeakRef.current?.deref()
        return mapInstance || null
      },
      
      // Utility methods
      isReady: () => !!map,
      getCenter: () => {
        try {
          const center = map?.getCenter()
          return center ? { lat: center.lat(), lng: center.lng() } : null
        } catch {
          return null
        }
      },
      getZoom: () => {
        try {
          return map?.getZoom() ?? null
        } catch {
          return null
        }
      },
      getBounds: () => {
        try {
          return map?.getBounds() ?? null
        } catch {
          return null
        }
      }
    }

    onControllerReady?.(controller)
    onLoad?.(map) // Notify that map is ready
  }, [map, getCurrentViewport, updateMapViewport, onControllerReady, onLoad, validateCoordinates, onError])

  // Set up error listener for Google Maps API errors
  useEffect(() => {
    if (!map) return

    const errorListener = google.maps.event.addListener(map, 'error', (error: any) => {
      onError?.({
        type: 'API_LOAD_FAILED',
        message: error?.message || 'Google Maps API error occurred'
      })
    })

    return () => {
      try {
        google.maps.event.removeListener(errorListener)
      } catch (error) {
        console.warn('Error removing map error listener:', error)
      }
    }
  }, [map, onError])

  // Set up native Google Maps listeners with proper cleanup
  useEffect(() => {
    if (!map) return

    const controller = new AbortController()
    const listeners: google.maps.MapsEventListener[] = []

    // Listener for immediate viewport changes (during drag/zoom)
    const boundsChangedListener = map.addListener('bounds_changed', () => {
      // Check if component is still mounted
      if (controller.signal.aborted) return
      
      const viewport = getCurrentViewport()
      if (viewport && onViewportChange) {
        onViewportChange(viewport)
      }
    })
    listeners.push(boundsChangedListener)

    // Listener for when viewport changes settle (user stops interacting)
    const idleListener = map.addListener('idle', () => {
      // Check if component is still mounted
      if (controller.signal.aborted) return
      
      const viewport = getCurrentViewport()
      if (!viewport) return

      // Only fire onViewportSettle if viewport actually changed
      const lastViewport = lastViewportRef.current
      if (!lastViewport ||
          lastViewport.center.lat !== viewport.center.lat ||
          lastViewport.center.lng !== viewport.center.lng ||
          lastViewport.zoom !== viewport.zoom) {

        lastViewportRef.current = viewport
        if (onViewportSettle) {
          onViewportSettle(viewport)
        }
      }
    })
    listeners.push(idleListener)

    // Comprehensive cleanup
    return () => {
      controller.abort()
      listeners.forEach(listener => {
        try {
          google.maps.event.removeListener(listener)
        } catch (error) {
          console.warn('Error removing map listener:', error)
        }
      })
    }
  }, [map, getCurrentViewport, onViewportChange, onViewportSettle])

  return null // This component only manages listeners
}

/**
 * Main map component with native listeners and MapController API
 */
export function Map({
  apiKey,
  defaultCenter,
  defaultZoom,
  className = '',
  style,
  onViewportChange,
  onViewportSettle,
  onError,
  onLoad,
  options,
  children,
  ariaLabel = "Interactive map",
  ariaDescription
}: MapProps) {
  const [controller, setController] = useState<MapController | null>(null)

  const handleControllerReady = useCallback((newController: MapController) => {
    setController(newController)
  }, [])

  return (
    <MapProvider controller={controller}>
      <APIProvider
        apiKey={apiKey}
        version="weekly"
      >
        <GoogleMap
          mapId="d92c76745cecbe7764151a60"
          className={`w-full h-full ${className}`}
          defaultCenter={defaultCenter}
          defaultZoom={defaultZoom}
          gestureHandling="greedy"
          disableDefaultUI={true}
          {...options}
          aria-label={ariaLabel}
          aria-describedby={ariaDescription ? "map-description" : undefined}
          style={{
            width: '100%',
            height: '100%',
            minHeight: '400px',
            ...style
          }}
        >
          {/* Accessibility description */}
          {ariaDescription && (
            <span id="map-description" className="sr-only">
              {ariaDescription}
            </span>
          )}
          
          <MapWithListeners
            onViewportChange={onViewportChange}
            onViewportSettle={onViewportSettle}
            onControllerReady={handleControllerReady}
            onError={onError}
            onLoad={onLoad}
          />
          
          {/* Additional children (markers, overlays, etc.) */}
          {children}
        </GoogleMap>
      </APIProvider>
    </MapProvider>
  )
}

// Re-export hooks and types from MapContext
export { useMapControls, useMapControlsOptional } from './MapContext'
export type { 
  MapViewport, 
  MapViewportUpdate,
  MapController, 
  MapError, 
  LatLng,
  MapState 
} from './MapContext'

// Export enhanced component types
export type { MapProps, MapOptions }
